import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  TrendingUp, ArrowLeft, Loader2, Calculator, BarChart3,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';

export default function TributarioSimulacoes() {
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [params, setParams] = useState({
    receita_bruta: 500000,
    percentual_desconto: 5,
    percentual_inadimplencia: 8,
    cenario: 'atual'
  });
  
  const navigate = useNavigate();

  const handleSimular = async () => {
    setLoading(true);
    try {
      const response = await api.post('/tributario/simular', params);
      setResultado(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao simular');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="p-6 space-y-6" data-testid="tributario-simulacoes">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tributario')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            Simulações Tributárias
          </h1>
          <p className="text-muted-foreground">Compare cenários e projete impostos</p>
        </div>
      </div>

      {/* Parâmetros */}
      <Card>
        <CardHeader>
          <CardTitle>Parâmetros da Simulação</CardTitle>
          <CardDescription>Ajuste os valores para ver o impacto tributário</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <Label>Receita Bruta Mensal</Label>
              <Input
                type="number"
                value={params.receita_bruta}
                onChange={(e) => setParams(prev => ({ ...prev, receita_bruta: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-sm text-muted-foreground">
                {formatCurrency(params.receita_bruta)}
              </p>
            </div>

            <div className="space-y-4">
              <Label>Percentual de Desconto: {params.percentual_desconto}%</Label>
              <Slider
                value={[params.percentual_desconto]}
                onValueChange={([v]) => setParams(prev => ({ ...prev, percentual_desconto: v }))}
                min={0}
                max={30}
                step={1}
              />
            </div>

            <div className="space-y-4">
              <Label>Percentual de Inadimplência: {params.percentual_inadimplencia}%</Label>
              <Slider
                value={[params.percentual_inadimplencia]}
                onValueChange={([v]) => setParams(prev => ({ ...prev, percentual_inadimplencia: v }))}
                min={0}
                max={30}
                step={1}
              />
            </div>
          </div>

          <Button onClick={handleSimular} disabled={loading} className="w-full md:w-auto">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <Calculator className="w-4 h-4 mr-2" />
                Simular Cenários
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Resultados */}
      {resultado && (
        <div className="space-y-4">
          {/* Cenário Atual */}
          {resultado.cenarios?.atual && (
            <Card className="border-purple-200">
              <CardHeader className="bg-purple-50">
                <CardTitle className="text-purple-800">{resultado.cenarios.atual.nome}</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Receita Líquida</p>
                    <p className="text-lg font-bold">{formatCurrency(resultado.cenarios.atual.receita_liquida)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Inadimplência</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(resultado.cenarios.atual.inadimplencia)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Tributos</p>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(resultado.cenarios.atual.total_tributos)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Carga Tributária</p>
                    <p className="text-lg font-bold">{resultado.cenarios.atual.carga_percentual}%</p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Detalhamento de Tributos</h4>
                  <div className="grid grid-cols-5 gap-2 text-sm">
                    <div className="text-center">
                      <p className="text-muted-foreground">PIS</p>
                      <p className="font-medium">{formatCurrency(resultado.cenarios.atual.tributos?.pis)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">COFINS</p>
                      <p className="font-medium">{formatCurrency(resultado.cenarios.atual.tributos?.cofins)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">ISS</p>
                      <p className="font-medium">{formatCurrency(resultado.cenarios.atual.tributos?.iss)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">IRPJ</p>
                      <p className="font-medium">{formatCurrency(resultado.cenarios.atual.tributos?.irpj)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">CSLL</p>
                      <p className="font-medium">{formatCurrency(resultado.cenarios.atual.tributos?.csll)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex gap-4">
                  <div className="flex-1 p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-sm text-green-600">Lucro Antes dos Tributos</p>
                    <p className="text-xl font-bold text-green-700">{formatCurrency(resultado.cenarios.atual.lucro_antes_tributos)}</p>
                  </div>
                  <div className="flex-1 p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm text-blue-600">Lucro Após Tributos</p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(resultado.cenarios.atual.lucro_apos_tributos)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparativo de Cenários */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reforma 2026 */}
            {resultado.cenarios?.reforma_2026 && (
              <Card className="border-amber-200">
                <CardHeader className="bg-amber-50">
                  <CardTitle className="text-amber-800 text-base">{resultado.cenarios.reforma_2026.nome}</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Total Tributos:</span>
                    <span className="font-bold">{formatCurrency(resultado.cenarios.reforma_2026.total_tributos)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Carga:</span>
                    <span className="font-bold">{resultado.cenarios.reforma_2026.carga_percentual}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Diferença vs Atual:</span>
                    <span className={`font-bold flex items-center gap-1 ${
                      resultado.cenarios.reforma_2026.diferenca_atual > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {resultado.cenarios.reforma_2026.diferenca_atual > 0 ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : resultado.cenarios.reforma_2026.diferenca_atual < 0 ? (
                        <ArrowDown className="w-4 h-4" />
                      ) : (
                        <Minus className="w-4 h-4" />
                      )}
                      {formatCurrency(Math.abs(resultado.cenarios.reforma_2026.diferenca_atual))}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{resultado.cenarios.reforma_2026.observacao}</p>
                </CardContent>
              </Card>
            )}

            {/* Crescimento 10% */}
            {resultado.cenarios?.crescimento_10 && (
              <Card className="border-green-200">
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-green-800 text-base">{resultado.cenarios.crescimento_10.nome}</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Receita Projetada:</span>
                    <span className="font-bold">{formatCurrency(resultado.cenarios.crescimento_10.receita_bruta)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Total Tributos:</span>
                    <span className="font-bold">{formatCurrency(resultado.cenarios.crescimento_10.total_tributos)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Aumento de Tributos:</span>
                    <span className="font-bold text-orange-600">
                      + {formatCurrency(resultado.cenarios.crescimento_10.aumento_tributos)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
